import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Loader2 } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { FileTypeIcon, VerifiedBadge } from "@/components/ui/documents";
import { formatFileSize } from "@/utils/formatFileSize";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { SigningStatusBadge } from "./SigningStatusBadge";
import { formatSigningDateTime } from "../format";
import { DateHint } from "@/domains/documents/components/atoms/DateHint";
import { EnclosedDocumentsMenu } from "@/domains/documents/components/molecules/EnclosedDocumentsMenu";
import {
  DocumentRowContextMenu,
  type DocumentMenuConfig,
} from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { useSigningPreview } from "../signingPreviewContext";
import {
  isBulkSelectable,
  signatureCountLabel,
  signerProgress,
  type SigningSort,
  type SigningSortField,
} from "../status";
import type { SigningListItem } from "../types";

// The standardized data-table design (Issues page is the source of truth, see
// Buildings/Documents): a plain header with one thin bottom border, uppercase
// muted `text-[10px]` labels, flat divider-separated rows — no filled header
// bar and no boxed container around the table.
const HEAD = "h-auto px-3 py-1.5 text-[10px]";
const SORTABLE_HEAD = cn(HEAD, "cursor-pointer select-none hover:text-foreground");
const CELL = "px-3 py-2.5";
// The checkbox column — the Documents list's 28px, on the same padding.
const CHECK_CELL = "w-10 pl-3 pr-0";

/**
 * Keeps a click or key press on a row's checkbox from reaching the row, whose
 * own handlers open that row in the detail panel (Space included).
 */
const stopRowEvent = {
  onClick: (event: { stopPropagation: () => void }) => event.stopPropagation(),
  onKeyDown: (event: { key: string; stopPropagation: () => void }) => {
    if (event.key === " " || event.key === "Enter") event.stopPropagation();
  },
};

/**
 * "2 of 3 signed" for a chain in flight. A session with no signer rows is a
 * legacy single-signer signing — there is no chain to report progress on, so it
 * says nothing rather than "0 of 0".
 */
function progressLabel(item: SigningListItem, template: string): string | null {
  if (item.signers.length === 0) return null;
  const { signed, total } = signerProgress(item.signers);
  if (total === 0) return null;
  return template.replace("{signed}", String(signed)).replace("{total}", String(total));
}

/** Active column shows its direction; the rest show the neutral affordance. */
function SortIcon({ isActive, direction }: { isActive: boolean; direction: "asc" | "desc" }) {
  if (!isActive) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
  return direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

interface SigningTableProps {
  items: SigningListItem[];
  selectedKey: string | null;
  onSelect: (item: SigningListItem) => void;
  sort: SigningSort;
  onSortChange: (field: SigningSortField) => void;
  /** The multi-selection feeding the bulk action bar. */
  checkedKeys: ReadonlySet<string>;
  onCheckedChange: (key: string, checked: boolean) => void;
  /** Header checkbox — (un)checks every visible container. */
  onCheckAll: (checked: boolean) => void;
  /**
   * The Documents page's right-click menu for a row, already bound to its
   * target(s); `null` (a session, which has no document yet) leaves the
   * browser's own menu.
   */
  rowMenu?: (item: SigningListItem) => DocumentMenuConfig | null;
}

/**
 * Row background, in the Documents list's vocabulary: a checked row carries the
 * selection tint, every other row the same subtle grey on hover. The row open in
 * the detail panel holds that grey rather than a tint of its own, so the tint
 * only ever means "part of the selection".
 */
function rowClass(isChecked: boolean, isOpen: boolean): string {
  if (isChecked) return "bg-primary/5 hover:bg-primary/10";
  if (isOpen) return "bg-[#f7f7f7] hover:bg-[#f7f7f7]";
  return "hover:bg-[#f7f7f7]";
}

/**
 * The signing list. One row per signed container or open session; clicking a
 * row drives the detail panel beside it.
 */
export function SigningTable({
  items,
  selectedKey,
  onSelect,
  sort,
  onSortChange,
  checkedKeys,
  onCheckedChange,
  onCheckAll,
  rowMenu,
}: SigningTableProps) {
  const { t, language } = useLanguage();
  const preview = useSigningPreview();
  const checkable = items.filter(isBulkSelectable);
  const checkedCount = checkable.filter((item) => checkedKeys.has(item.key)).length;
  const allChecked = checkable.length > 0 && checkedCount === checkable.length;

  const sortableHead = (
    field: SigningSortField,
    label: string,
    className?: string,
    hint?: string
  ) => (
    <TableHead className={cn(SORTABLE_HEAD, className)}>
      <button
        type="button"
        onClick={() => onSortChange(field)}
        aria-label={label}
        // The UA stylesheet resets `text-transform` on form controls, so the
        // header's uppercase has to be restated on the button itself.
        className="inline-flex items-center gap-1 uppercase tracking-wide"
      >
        {hint ? <DateHint hint={hint}>{label}</DateHint> : label}
        <SortIcon isActive={sort.field === field} direction={sort.direction} />
      </button>
    </TableHead>
  );

  return (
    // role="grid" makes the rows' aria-selected valid for AT (HTML-AAM maps
    // td → gridcell inside a grid).
    <Table role="grid" aria-label={t.signing.title}>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className={cn(HEAD, CHECK_CELL)}>
            {checkable.length > 0 && (
              <Checkbox
                checked={allChecked}
                indeterminate={checkedCount > 0 && !allChecked}
                onCheckedChange={(checked) => onCheckAll(checked === true)}
                aria-label={t.signing.selection.selectAll}
                className="align-middle"
              />
            )}
          </TableHead>
          {sortableHead("title", t.signing.columns.document)}
          {sortableHead("sender", t.signing.columns.sender, "hidden w-[150px] lg:table-cell")}
          {sortableHead(
            "created",
            t.signing.columns.created,
            "hidden w-[140px] lg:table-cell",
            t.signing.columns.createdTooltip
          )}
          <TableHead className={cn(HEAD, "w-[150px]")}>{t.signing.columns.status}</TableHead>
          <TableHead className={cn(HEAD, "w-10")} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const isSelected = item.key === selectedKey;
          const isChecked = isBulkSelectable(item) && checkedKeys.has(item.key);
          const subtitle = [
            item.size != null ? formatFileSize(item.size) : null,
            // A finished container states how many signatures it holds; a chain
            // in flight states how far through it is ("2 of 3 signed"), which
            // is the only place the list shows chain progress at all.
            item.kind === "container"
              ? signatureCountLabel(item.signers.length, t.signing.signatureCount)
              : progressLabel(item, t.signing.chain.progress),
            item.kind === "session" ? item.errorMessage : null,
          ]
            .filter(Boolean)
            .join(" · ");

          const menu = rowMenu?.(item) ?? null;
          const canOpen = preview?.canOpenItem(item) ?? false;
          const isContainer = preview?.isContainerItem(item) ?? false;

          const row = (
            <TableRow
              key={item.key}
              // Pressing a row moves the detail sheet rather than closing it
              // (SIGNING_ROW_ATTRIBUTE in SigningDetailSheet), and closing the
              // sheet hands focus back here.
              data-signing-row={item.key}
              onClick={() => onSelect(item)}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(item);
                }
              }}
              aria-selected={isSelected}
              className={cn("cursor-pointer", rowClass(isChecked, isSelected))}
            >
              <TableCell className={cn(CELL, CHECK_CELL)}>
                {isBulkSelectable(item) && (
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => onCheckedChange(item.key, checked === true)}
                    aria-label={t.signing.selection.selectRow.replace("{title}", item.title)}
                    className="align-middle"
                    {...stopRowEvent}
                  />
                )}
              </TableCell>
              <TableCell className={CELL}>
                <div className="flex min-w-0 items-center gap-3">
                  <FileTypeIcon filename={item.title} />
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {canOpen && preview ? (
                        // Opens the file the way the Documents list's title
                        // does: a signed PDF in the viewer, a container in the
                        // signature panel. The row still becomes the selection.
                        <button
                          type="button"
                          className="min-w-0 cursor-pointer truncate text-left text-[13px] font-semibold hover:text-primary hover:underline"
                          aria-busy={preview.openingId === item.documentId}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelect(item);
                            preview.openItem(item);
                          }}
                          onKeyDown={stopRowEvent.onKeyDown}
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span className="truncate text-[13px] font-semibold">{item.title}</span>
                      )}
                      {item.documentId != null && preview?.openingId === item.documentId && (
                        <Loader2
                          className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground"
                          aria-label={t.common.loading}
                        />
                      )}
                      {isContainer && item.documentId != null && (
                        // The Documents list's inline peek at what the
                        // container encloses; an entry opens the signature
                        // panel on that document.
                        <span className="flex shrink-0" onKeyDown={stopRowEvent.onKeyDown}>
                          <EnclosedDocumentsMenu
                            containerId={String(item.documentId)}
                            containerFilename={item.title}
                          />
                        </span>
                      )}
                      <VerifiedBadge
                        verifiedAt={item.verifiedAt}
                        verifiedByName={item.verifiedByName}
                      />
                      {/* Belongs to no project, so it has no project page of its
                          own — it rides along in every one. Say so on the row
                          rather than letting it read as this project's. */}
                      {item.unfiled && (
                        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          {t.signing.unfiled.badge}
                        </span>
                      )}
                    </div>
                    {subtitle && (
                      <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell
                className={cn(CELL, "hidden truncate text-xs text-muted-foreground lg:table-cell")}
              >
                {item.senderName || "—"}
              </TableCell>
              <TableCell
                className={cn(
                  CELL,
                  "hidden whitespace-nowrap text-xs text-muted-foreground lg:table-cell"
                )}
              >
                <DateHint hint={t.signing.columns.createdTooltip}>
                  {formatSigningDateTime(item.createdAt, language)}
                </DateHint>
              </TableCell>
              <TableCell className={CELL}>
                <SigningStatusBadge status={item.status} />
              </TableCell>
              <TableCell className={cn(CELL, "text-muted-foreground")}>
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </TableCell>
            </TableRow>
          );

          return menu ? (
            <DocumentRowContextMenu key={item.key} variant="document" {...menu}>
              {row}
            </DocumentRowContextMenu>
          ) : (
            row
          );
        })}
      </TableBody>
    </Table>
  );
}
